import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'sharedUi/Button';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { personnelService, type PersonnelQueryParams } from '../../services/personnel.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { Personnel } from '../../types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import { ProjectCell } from '../../components/common/project-cell';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import { formatPhone } from '../../utils/phone-display';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [{ field: 'name', header: 'Name', filter: true }];

const PersonnelList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const navigate = useAppNavigate();
  const { navigateToView, navigateToEdit } = useSecureNavigation({
    entity: 'personnel',
    basePath: '/personnel',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.PERSONNEL) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.PERSONNEL);
  const canDelete = useCanDelete(JOB_NAMES.PERSONNEL);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPersonnelId, setSelectedPersonnelId] = useState<string | null>(null);
  const [selectedPersonnelName, setSelectedPersonnelName] = useState<string | null>(null);

  // Filter states
  const [searchText, setSearchText] = useState<string>('');
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Pagination states for server-side pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting states
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);

  // Per-column filter state
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  // Debounce search text (500ms delay)
  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 10000,
    });
  }, []);

  // Load the caller's accessible projects once (for the Project filter + column).
  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);

  const fetchPersonnel = useCallback(async (
    page: number = 1,
    size: number = pageSize,
    sortBy: string | null = sortField,
    order: 1 | -1 | null = sortOrder,
    activeFilters: Record<string, any> = filters
  ) => {
    try {
      setLoading(true);

      // Build query parameters for server-side pagination
      const params: PersonnelQueryParams = {
        page,
        page_size: size,
        include_deleted: includeDeleted,
        // Active/Inactive concept removed from the UI — always show active records.
        isActive: true,
      };

      // Add search parameter for server-side search (using debounced value)
      if (debouncedSearchText?.trim()) {
        params.search = debouncedSearchText.trim();
      }

      // Add sorting parameters
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_order = order === 1 ? 'asc' : 'desc';
      }

      const filtersParam = buildColumnFiltersParam(activeFilters);
      if (filtersParam) params.filters = filtersParam;

      if (selectedProjectId) params.app_id = selectedProjectId;
      params.include_global = showCore;

      const response = await personnelService.getAll(params);

      // Map _id to id for DataGrid compatibility
      const mappedData = (response.data || []).map(p => ({ ...p, id: p._id }));

      setPersonnel(mappedData);
      // Handle both totalItems and total for backward compatibility
      const total = response.pagination?.totalItems ?? (response.pagination as any)?.total ?? 0;
      setTotalRecords(total);
    } catch (err: any) {
      console.error('Failed to fetch personnel:', err);
      showError(extractErrorMessage(err, 'Failed to load personnel. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [includeDeleted, debouncedSearchText, selectedProjectId, showCore, pageSize, sortField, sortOrder, filters, showError]);

  // Handle DataTable pagination changes
  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchPersonnel(newPage, event.rows, sortField, sortOrder);
  };

  // Reset to first page and fetch when filters change
  useEffect(() => {
    setFirst(0);
    fetchPersonnel(1, pageSize);
  }, [includeDeleted, debouncedSearchText, selectedProjectId, showCore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle sort event - server-side sorting
  const handleSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1 | null;

    setSortField(newSortField);
    setSortOrder(newSortOrder);

    // Reset to first page when sorting changes
    setFirst(0);
    fetchPersonnel(1, pageSize, newSortField, newSortOrder);
  };

  // Handle per-column filter changes
  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchPersonnel(1, pageSize, sortField, sortOrder, next);
  };

  const handleView = (id: string | number) => {
    navigateToView(String(id));
  };

  const handleEdit = (id: string | number) => {
    navigateToEdit(String(id));
  };

  const handleDeleteClick = (id: string | number, name: string) => {
    setSelectedPersonnelId(id as string);
    setSelectedPersonnelName(name);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPersonnelId) return;

    const personnelName = selectedPersonnelName || 'personnel';

    try {
      setDeleteLoading(true);
      await personnelService.delete(selectedPersonnelId);
      setDeleteDialogOpen(false);
      setSelectedPersonnelId(null);
      setSelectedPersonnelName(null);
      fetchPersonnel(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (err: any) {
      console.error('Failed to delete personnel:', err);
      setDeleteDialogOpen(false);
      setSelectedPersonnelId(null);
      setSelectedPersonnelName(null);
      // Extract error message and replace the ID with personnel name
      let errorMsg = extractErrorMessage(err, `Failed to delete personnel ${personnelName}`);
      if (selectedPersonnelId && errorMsg.includes(selectedPersonnelId)) {
        errorMsg = errorMsg.replace(selectedPersonnelId, personnelName);
      }
      showError(errorMsg);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedPersonnelId(null);
    setSelectedPersonnelName(null);
  };

  const columns: SaasColumn<Personnel>[] = useMemo(() => {
    const baseColumns: SaasColumn<Personnel>[] = [
      {
        field: 'name',
        header: 'Name',
        sortable: true,
        filter: true,
        hideable: false,
      },
      {
        field: 'phoneNo',
        header: 'Phone',
        body: (rowData: Personnel) => formatPhone(rowData.phoneNo) || '-',
      },
      {
        field: 'unit',
        header: 'Unit',
        body: (rowData: Personnel) => rowData.unit?.name || (rowData as any).unitName || '-',
      },
      {
        field: 'department',
        header: 'Department',
        body: (rowData: Personnel) => rowData.department?.name || '-',
      },
      {
        field: 'ministry',
        header: 'Ministry',
        body: (rowData: Personnel) => (rowData as any).ministry?.name || '-',
      },
      {
        field: 'designation',
        header: 'Designation',
        body: (rowData: Personnel) =>
          (rowData as any).designation?.name || rowData.units?.[0]?.designation?.name || '-',
      },
      {
        field: 'appId',
        header: 'Project',
        body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} />,
      },
    ];

    // Only show Status column when includeDeleted is active
    if (includeDeleted) {
      baseColumns.push({
        field: 'status',
        header: 'Status',
        body: (rowData: Personnel) => {
          const deleted = (rowData as any).isDelete;
          return (
            <span
              className={`saas-status-dot ${deleted ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            >
              {deleted ? 'Deleted' : 'Active'}
            </span>
          );
        },
      });
    }

    // Always add actions column at the end
    baseColumns.push({
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (rowData: Personnel) => (
        <div className="flex gap-1 justify-center">
          <button aria-label="View"
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            onClick={() => handleView(rowData._id)}
            data-testid="PersonnelList.Action.View"
            data-pr-tooltip="View"
          >
            <i className="pi pi-eye text-green-600 dark:text-green-400" style={{ fontSize: '1.05rem' }} />
          </button>

          {!(rowData as any).isDelete && (
            <>
              {canUpdate && (
                <button aria-label="Edit"
                  className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  onClick={() => handleEdit(rowData._id)}
                  data-testid="PersonnelList.Action.Edit"
                  data-pr-tooltip="Edit"
                >
                  <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} />
                </button>
              )}

              {canDelete && (
                <button aria-label="Delete"
                  className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  onClick={() => handleDeleteClick(rowData._id, rowData.name)}
                  data-testid="PersonnelList.Action.Delete"
                  data-pr-tooltip="Delete"
                >
                  <i className="pi pi-trash text-red-600" style={{ fontSize: '1.05rem' }} />
                </button>
              )}
            </>
          )}
        </div>
      ),
    });

    return baseColumns;
  }, [canUpdate, canDelete, includeDeleted, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.PERSONNEL}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-Personnel-List">
        {!embedded && (
          <PageHeader
            icon="pi pi-users"
            title="Personnel"
            subtitle="Manage police personnel records and assignments"
            testId="Personnel.Header"
          />
        )}

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="personnel"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={personnel}
            loading={loading}
            dataKey="_id"
            emptyMessage="No personnel found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search by name, User ID"
            testId="PersonnelList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {!appId && accessibleProjects.length > 1 && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px', width: '160px' }}
                    data-testid="PersonnelList.Filter.Project"
                    resetFilterOnHide
                  />
                )}
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="PersonnelList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <RefreshButton
                  onClick={() => fetchPersonnel(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                  testId="PersonnelList.Button.Refresh"
                />
                {canCreate && (
                  <Button
                    label="Create Personnel"
                    icon="pi pi-user-plus"
                    onClick={() => navigate('/personnel/create')}
                    testId="PersonnelList.Button.Onboarding"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this personnel record? This action cannot be undone."
          testId="PersonnelList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default PersonnelList;
